/* import __COLOCATED_TEMPLATE__ from './transfer-amount-step.hbs'; */
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor10;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { RoundingMode } from '@square/dashboard-protos/models/squareup/common/currency_codes';
import bankersRound from '@square/dashboard-utilities/utils/math/bankers-round';
import { toFixedRoundedDown } from '@banking/ui/utils/to-fixed-rounded-down';
import { Money as CommonCurrency } from '@square/dashboard-protos/models/squareup/common/currency';
import { Money as CommonMoney } from '@square/dashboard-protos/models/squareup/connect/v2/common/money';
import formatMoney from '@banking/ui/utils/format-money';
import { clientFlowEnumToString } from '@banking/ui/utils/flexible-transfers-utils';
import { Validation } from '@banking/ui/utils/validation';
let TransferAmountStep = (_class = class TransferAmountStep extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "formatter", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "currencyCode", _descriptor3, this);
    _initializerDefineProperty(this, "amountInCents", _descriptor4, this);
    _initializerDefineProperty(this, "fee", _descriptor5, this);
    _initializerDefineProperty(this, "transferAmount", _descriptor6, this);
    _initializerDefineProperty(this, "amountToDisplay", _descriptor7, this);
    _initializerDefineProperty(this, "isInvalid", _descriptor8, this);
    _initializerDefineProperty(this, "isEmpty", _descriptor9, this);
    _initializerDefineProperty(this, "validation", _descriptor10, this);
  }
  get nativeFlow() {
    return clientFlowEnumToString(this.args.step.transferAmount?.secondaryButton?.action?.clientFlow);
  }
  get feePercentage() {
    const feePercentage = this.args.step.transferAmount?.percentageFee?.feeBasisPoints ?? 0;
    return feePercentage / 100;
  }
  get feeFormatted() {
    const negativeFee = CommonCurrency.create({
      amount: !this.fee?.amount || this.fee.amount === 0 ? 0 : this.fee.amount * -1,
      currencyCode: this.currencyCode
    });
    return formatMoney(negativeFee, this.formatter);
  }
  get transferAmountFormatted() {
    if (this.transferAmount) {
      return formatMoney(this.transferAmount, this.formatter);
    }
    return formatMoney(CommonCurrency.create({
      amount: 0,
      currencyCode: this.currencyCode
    }), this.formatter);
  }

  // We use CommonCurrency.create in all other Money object creation because
  // bb-frontend uses the /common/currency proto definition.
  // We use CommonMoney.create whenever we need to pass an amount to be
  // displayed by the currency-input component such as the initialAmount.
  get initialAmount() {
    let initialAmt = undefined;
    // If the user edited the amount and clicked back to get back to the amount
    // screen, show the amount they most recently entered.
    if (this.args.editedTransferAmount && this.args.editedTransferAmount.amount > 0) {
      initialAmt = CommonMoney.create({
        amount: this.args.editedTransferAmount.amount,
        // bb-frontend currencyCode is exactly the same type as Currency.
        currency: this.currencyCode
      });
    } else if (
    // Else, if we have a default amount from bb-frontend data, show that amount.
    this.args.step?.transferAmount?.amount && this.args.step.transferAmount.amount.amount > 0) {
      initialAmt = CommonMoney.create({
        amount: this.args.step.transferAmount.amount.amount,
        currency: this.currencyCode
      });
    }
    return initialAmt;
  }
  validateInput(amount) {
    this.validation = new Validation();
    const {
      minAmount,
      maxAmount
    } = this.args.step?.transferAmount ?? {};
    const {
      belowMin,
      aboveMax
    } = this.args.step?.transferAmount?.rangeErrorMessages ?? {};
    this.isInvalid = true;
    if (minAmount && belowMin && amount < minAmount.amount) {
      this.validation = new Validation(belowMin);
      return;
    }
    if (maxAmount && aboveMax && amount > maxAmount.amount) {
      this.validation = new Validation(aboveMax);
      return;
    }
    this.isInvalid = false;
  }
  calculateFeeAndAmount(amountInCents) {
    // Standard speed transfer (no fee calc needed)
    if (!this.args.step?.transferAmount?.percentageFee && amountInCents > 0) {
      this.validateInput(amountInCents);
      return;
    }
    const {
      feeBasisPoints,
      roundingMode
    } = this.args.step?.transferAmount?.percentageFee ?? {};

    // Instant transfer
    if (feeBasisPoints !== undefined) {
      const decimalFeeBasisPoints = feeBasisPoints / 100;
      const percentageFeeBasisPoints = decimalFeeBasisPoints / 100;
      const rawFee = amountInCents * percentageFeeBasisPoints;
      let roundedFee = 0;
      switch (roundingMode) {
        case RoundingMode.DOWN:
          roundedFee = Math.floor(rawFee);
          break;
        case RoundingMode.UP:
          roundedFee = Math.ceil(rawFee);
          break;
        case RoundingMode.BANKERS:
          roundedFee = bankersRound(rawFee, 2);
          break;
        case RoundingMode.DOWN_ON_HALF:
          roundedFee = Number(toFixedRoundedDown(rawFee, 2));
          break;
        default:
          // RoundingMode.PLAIN
          roundedFee = Math.round(rawFee);
      }
      this.fee = CommonCurrency.create({
        amount: roundedFee,
        currencyCode: this.currencyCode
      });
      this.transferAmount = CommonCurrency.create({
        amount: amountInCents - roundedFee,
        currencyCode: this.currencyCode
      });
      this.validateInput(amountInCents);
    }
  }
  handleMoneyChange(money) {
    this.amountInCents = money?.amount;
    if (!this.amountInCents || this.amountInCents === 0) {
      this.fee = CommonCurrency.create({
        amount: 0,
        currencyCode: this.currencyCode
      });
      this.transferAmount = CommonCurrency.create({
        amount: 0,
        currencyCode: this.currencyCode
      });
      this.isEmpty = true;
      return;
    }
    this.isEmpty = false;
    this.amountToDisplay = money;
    this.calculateFeeAndAmount(this.amountInCents);
  }
  handleOnNext() {
    if (this.isEmpty) {
      this.validation = new Validation(this.intl.t('banking.flexibleTransfers.transferAmountStep.noAmountError'));
      this.isInvalid = true;
      return;
    }
    if (!this.validation?.isValid) {
      return;
    }
    this.args.updateStep(this.args?.step?.transferAmount?.primaryButton, {
      amount: CommonCurrency.create({
        amount: this.amountInCents,
        currencyCode: this.currencyCode
      }),
      calculatedFee: CommonCurrency.create({
        amount: this.fee?.amount,
        currencyCode: this.currencyCode
      })
    });
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "formatter", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "currencyCode", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.step.transferAmount?.amount?.currencyCode;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "amountInCents", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "fee", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "transferAmount", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "amountToDisplay", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "isInvalid", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "isEmpty", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "validation", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "handleMoneyChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleMoneyChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleOnNext", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleOnNext"), _class.prototype), _class);
export { TransferAmountStep as default };